import React from "react";
import { BlockMath, InlineMath } from "react-katex";
import ABCDInput from "../components/inputs/ABCDInput";
import TruthInput from "../components/inputs/TruthInput";
import MaturaFunctionGraph from "../components/GraphPlotter/MaturaFunctionGraph";
import { ThalesCrossedDiagram } from "../components/diagrams/ThalesTriangleDiagram";
import MaturaFourPointCircleAnglesDiagram from "../components/diagrams/MaturaFourPointCircleAnglesDiagram";
import "./MaturaMay2026ClosedTaskSupport.css";

const LETTERS = ["A", "B", "C", "D"];

export const pick = (items) => items[Math.floor(Math.random() * items.length)];

export function shuffle(items) {
  const result = [...items];
  for (let index = result.length - 1; index > 0; index -= 1) {
    const swapIndex = Math.floor(Math.random() * (index + 1));
    [result[index], result[swapIndex]] = [result[swapIndex], result[index]];
  }
  return result;
}

export function makeOptions(correctLatex, wrongLatex, fixedLabels = null) {
  if (fixedLabels) {
    const options = fixedLabels.map(([label, latex]) => ({
      label,
      latex,
      isCorrect: latex === correctLatex,
    }));
    return { options, correctIndex: options.findIndex((item) => item.isCorrect) };
  }

  const raw = [
    { latex: correctLatex, isCorrect: true },
    ...wrongLatex
      .filter((item) => item !== correctLatex)
      .slice(0, 3)
      .map((latex) => ({ latex, isCorrect: false })),
  ];
  while (raw.length < 4) raw.push({ latex: String(raw.length + 1), isCorrect: false });
  const options = shuffle(raw).map((item, index) => ({ ...item, label: LETTERS[index] }));
  return { options, correctIndex: options.findIndex((item) => item.isCorrect) };
}

export function makeClosedTask({ type, taskNumber, variant, data, tags = [] }) {
  return {
    type,
    data,
    source: {
      variant,
      exam: "MATURA",
      session: variant === "original" ? "2026_MAJ" : undefined,
      taskNumber,
      basedOn: "MMAP-P0-100-A-2605",
    },
    tags: ["matura", "cke-2026-maj", ...tags],
  };
}

export function makeSimpleClosedTask({
  type,
  taskNumber,
  variant,
  prompt,
  promptLatex = null,
  promptAfterLatex = null,
  latex = null,
  correct,
  wrong,
  tags = [],
  explanationSteps = [],
  alternativeExplanation = null,
  fixedOptions = null,
  data = {},
}) {
  const { options, correctIndex } = makeOptions(correct, wrong, fixedOptions);
  return makeClosedTask({
    type,
    taskNumber,
    variant,
    tags,
    data: {
      ...data,
      instruction: "Dokończ zdanie. Wybierz właściwą odpowiedź spośród podanych.",
      prompt,
      promptLatex,
      promptAfterLatex,
      latex,
      options,
      correctIndex,
      explanationSteps,
      alternativeExplanation,
    },
  });
}

export function normalizeMay2026Variant(options = {}) {
  return options.variant === "original" && options.session === "2026_MAJ"
    ? "original"
    : "random";
}

export function shiftedSquare(variable, center) {
  if (center === 0) return `${variable}^2`;
  return center > 0
    ? `(${variable}-${center})^2`
    : `(${variable}+${Math.abs(center)})^2`;
}

export function rationalLatex(value) {
  if (Number.isInteger(value)) return String(value);
  for (let denominator = 2; denominator <= 12; denominator += 1) {
    const numerator = Math.round(value * denominator);
    if (Math.abs(value - numerator / denominator) < 1e-9) {
      const sign = numerator < 0 ? "-" : "";
      return `${sign}\\frac{${Math.abs(numerator)}}{${denominator}}`;
    }
  }
  return String(Number(value.toFixed(4))).replace(".", "{,}");
}

export function CircleAnglesDiagram({ data }) {
  const center = 130;
  const radius = 95;
  const point = (degrees) => {
    const radians = (degrees * Math.PI) / 180;
    return [center + radius * Math.cos(radians), center - radius * Math.sin(radians)];
  };
  const A = point(205);
  const B = point(335);
  const C = point(80);
  return (
    <svg viewBox="0 0 260 260" role="img" aria-label="Okrąg z zaznaczonymi kątami" style={{ width: "min(100%, 320px)", margin: "0 auto" }}>
      <circle cx={center} cy={center} r={radius} fill="none" stroke="currentColor" strokeWidth="2" />
      <path d={`M${A[0]} ${A[1]} L${C[0]} ${C[1]} L${B[0]} ${B[1]}`} fill="none" stroke="currentColor" strokeWidth="2" />
      <path d={`M${A[0]} ${A[1]} L${center} ${center} L${B[0]} ${B[1]}`} fill="none" stroke="currentColor" strokeWidth="2" />
      <circle cx={center} cy={center} r="3" fill="currentColor" />
      <text fill="currentColor" fontFamily="KaTeX_Main, serif" x={C[0] + 5} y={C[1] - 5}>C</text>
      <text fill="currentColor" fontFamily="KaTeX_Main, serif" x={A[0] - 14} y={A[1] + 14}>A</text>
      <text fill="currentColor" fontFamily="KaTeX_Main, serif" x={B[0] + 5} y={B[1] + 14}>B</text>
      <text fill="currentColor" fontFamily="KaTeX_Main, serif" x={center + 6} y={center - 6}>O</text>
      <text fill="#8b5cf6" fontFamily="KaTeX_Main, serif" fontSize="18" x={C[0] - 35} y={C[1] + 28}>{`${data.ins}\u00B0`}</text>
      <text fill="#ff9800" fontFamily="KaTeX_Main, serif" fontSize="18" x={center - 8} y={center + 36}>{`${data.central}\u00B0`}</text>
    </svg>
  );
}

export function buildLinearGraphProps(data) {
  const interceptPoint = [data.point1, data.point2].find((point) => point[1] === 0);
  const xIntercept = interceptPoint?.[0] ?? (-data.b / data.a);
  const points = data.showInterceptPoints === false
    ? {}
    : {
        P: { x: data.point1[0], y: data.point1[1], showLabel: false },
        Q: { x: data.point2[0], y: data.point2[1], showLabel: false },
      };
  const arcs = [];

  if (data.showSlopeAngle) {
    const acuteAngle = (Math.atan(Math.abs(data.a)) * 180) / Math.PI;
    const angle = data.a > 0 ? acuteAngle : 180 - acuteAngle;
    const labelAngle = (angle * Math.PI) / 360;
    points.slopeAngle = {
      x: xIntercept + 1.35 * Math.cos(labelAngle),
      y: 1.35 * Math.sin(labelAngle),
      label: "α",
      showDot: false,
      textColor: "#8b5cf6",
      showLabel: true,
      textPosition: "middle center",
    };
    arcs.push({
      center: { x: xIntercept, y: 0 },
      radius: 0.9,
      startDeg: 0,
      endDeg: angle,
      color: "#8b5cf6",
      width: 3,
    });
  }

  return { points, arcs };
}

// Snapshoty w MongoDB są danymi JSON. Starsze zadania mogły jednak zapisać
// fragment JSX jako zwykły obiekt { type, props }, którego React nie może
// bezpośrednio wyrenderować. Odtwarzamy wyłącznie bezpieczne elementy tekstowe
// i matematyczne, zamiast przekazywać taki obiekt jako dziecko komponentu.
function renderSnapshotContent(value, key = "snapshot-content") {
  if (value == null || typeof value === "boolean") return null;
  if (typeof value === "string" || typeof value === "number") return String(value);
  if (React.isValidElement(value)) return value;

  if (Array.isArray(value)) {
    return value.map((item, index) => (
      <React.Fragment key={`${key}-${index}`}>
        {renderSnapshotContent(item, `${key}-${index}`)}
      </React.Fragment>
    ));
  }

  if (typeof value !== "object") return null;

  const props = value.props && typeof value.props === "object" ? value.props : {};
  if (props.math != null && !value.type) {
    return <InlineMath math={String(props.math)} />;
  }

  if (typeof value.type === "string") {
    return React.createElement(
      value.type,
      {
        key,
        style: props.style,
        className: props.className,
      },
      renderSnapshotContent(props.children, `${key}-children`)
    );
  }

  return renderSnapshotContent(props.children, `${key}-children`);
}

export function renderClosedTask({ data }) {
  const linearGraph = data.kind === "linearGraph" ? buildLinearGraphProps(data) : null;
  const graph = linearGraph && <MaturaFunctionGraph equations={[{expr:`y=${data.a}*x+(${data.b})`}]} points={linearGraph.points} arcs={linearGraph.arcs} xRange={[-6,6]} yRange={[-6,6]}/>;
  return <div style={{display:"grid",gap:10}}>{data.context&&<div>{renderSnapshotContent(data.context, "context")}</div>}{data.graphBeforeInstruction&&graph}<p>{data.instruction}</p>{data.prompt&&<div>{renderSnapshotContent(data.prompt, "prompt")}{data.promptLatex&&<> <InlineMath math={data.promptLatex}/>{renderSnapshotContent(data.promptAfterLatex, "prompt-after")}</>}</div>}{data.latex&&<BlockMath math={data.latex}/>} {!data.graphBeforeInstruction&&graph} {data.kind==="circleAngles"&&<MaturaFourPointCircleAnglesDiagram inscribedDeg={data.ins} centralDeg={data.central} resultDeg={data.result}/>} {data.kind==="parallelSegments"&&<ThalesCrossedDiagram labels={{OA:data.OA,OB:data.OB,OC:data.OC}}/>}{data.afterLatex&&<div>{renderSnapshotContent(data.afterLatex, "after")}</div>}{data.answerMode!=="truth"&&data.options?.map((option)=><div key={option.label}><InlineMath math={`${option.label}.\\;${option.latex}`}/></div>)}</div>;
}

export function renderClosedInput({ data }, value = {}, onChange, disabled) {
  if (data.answerMode === "truth") {
    const set = (key, next) => onChange?.({ ...value, [key]: next });
    return <div style={{display:"grid",gap:8}}><TruthInput value={value.s1||null} onChange={(next)=>set("s1",next)} disabled={disabled}/><TruthInput value={value.s2||null} onChange={(next)=>set("s2",next)} disabled={disabled}/></div>;
  }
  return <ABCDInput options={data.options.map((option,index)=>({value:index,label:option.label,textNode:option.label}))} value={value.choiceIndex??null} onChange={(index)=>onChange?.({choiceIndex:index,label:data.options[index].label,latex:data.options[index].latex})} disabled={disabled}/>;
}

export function checkClosedAnswer({ data }, value = {}) {
  if (data.answerMode === "truth") return value.s1 === (data.statementTruth[0] ? "P" : "F") && value.s2 === (data.statementTruth[1] ? "P" : "F");
  return value.choiceIndex === data.correctIndex;
}

export function renderClosedAnswer({ data }) {
  if (data.answerMode === "truth") return <InlineMath math={`${data.statementTruth[0]?"P":"F"},\\;${data.statementTruth[1]?"P":"F"}`}/>;
  const option = data.options[data.correctIndex];
  return <InlineMath math={`${option.label}.\\;${option.latex}`}/>;
}

export function renderClosedUserAnswer(value = {}, task = {}) {
  const data = task?.data || {};
  if (data.answerMode === "truth") {
    if (!value?.s1 && !value?.s2) return <i>(brak odpowiedzi)</i>;
    return <InlineMath math={`${value?.s1 || "?"},\\;${value?.s2 || "?"}`} />;
  }

  const choiceIndex = Number.isInteger(value?.choiceIndex) ? value.choiceIndex : null;
  const option = choiceIndex == null ? null : data.options?.[choiceIndex];
  if (!option) return <i>(brak odpowiedzi)</i>;
  return <InlineMath math={`${option.label}.\\;${option.latex}`} />;
}

export function AlternativeExplanation({ solution }) {
  if (!solution) return null;

  return (
    <details className="matura-alternative-explanation">
      <summary className="matura-alternative-explanation__summary">
        {renderSnapshotContent(solution.title || "Inny sposób")}
      </summary>
      <div className="matura-alternative-explanation__content">
        {solution.intro && <p className="matura-alternative-explanation__intro">{renderSnapshotContent(solution.intro)}</p>}
        {(solution.steps || []).map((step, index) => (
          <div className="matura-alternative-explanation__step" key={index}>
            {step.title && (
              <p>
                <strong>Krok {index + 1}.</strong> {renderSnapshotContent(step.title)}
              </p>
            )}
            {step.latex && <BlockMath math={step.latex} />}
          </div>
        ))}
        {solution.note && <p className="matura-alternative-explanation__note">{renderSnapshotContent(solution.note)}</p>}
      </div>
    </details>
  );
}

export function renderClosedExplanation({ data }) {
  const steps = data.explanationSteps || (data.explanationLines || []).map((latex, index) => ({
    title: data.explanationTitles?.[index] || "",
    latex,
  }));
  const option = data.options?.[data.correctIndex];
  const truthValues = data.statementTruth?.map((value) => (value ? "P" : "F"));
  const conclusion = data.answerMode === "truth" && truthValues?.length === 2
    ? (
      <p>
        <strong>Wniosek.</strong> Pierwsze zdanie jest {truthValues[0] === "P" ? "prawdziwe" : "fałszywe"},
        a drugie {truthValues[1] === "P" ? "prawdziwe" : "fałszywe"}.
        <> Odpowiedź: <InlineMath math={`${truthValues[0]},\\;${truthValues[1]}`} />.</>
      </p>
    )
    : option
      ? <p><strong>Wniosek.</strong> Poprawna odpowiedź: <InlineMath math={`${option.label}.\\;${option.latex}`} />.</p>
      : null;

  return (
    <div style={{ display: "grid", gap: 12 }}>
      {steps.map((step, index) => (
        <div key={index}>
          <p><strong>Krok {index + 1}.</strong> {renderSnapshotContent(step.title)}</p>
          {step.latex && <BlockMath math={step.latex} />}
          {(step.parts || []).map((part, partIndex) => (
            <React.Fragment key={partIndex}>
              {part.text && <p>{renderSnapshotContent(part.text)}</p>}
              {part.latex && <BlockMath math={part.latex} />}
            </React.Fragment>
          ))}
        </div>
      ))}
      {conclusion}
      <AlternativeExplanation solution={data.alternativeExplanation} />
    </div>
  );
}
